import { ChangeDetectionStrategy, Component, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';

import { Store } from '@ngrx/store';
import { Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';

import { TranslocoService } from '@ngneat/transloco';

import { DynamicDialogRef } from 'primeng/dynamicdialog';

import { EnvironmentService, NotificationService } from '@core/services';

import { LoginRequest } from '#types/auth';

import { CONSTANTS } from '@auth/constants';
import { AuthService, AuthUtilService } from '@auth/services';
import { AuthActions } from '@auth/state';

@Component({
  templateUrl: './timeout.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TimeoutComponent implements OnInit, OnDestroy {
  destroy$ = new Subject<void>();

  form: FormGroup;

  dialogId = CONSTANTS.DIALOG_TIMEOUT;

  translate = (key: string | string[], params?: Record<string, string>, lang?: string) =>
    this.translocoService.translate(key, params, lang);

  constructor(
    private store: Store,
    private translocoService: TranslocoService,
    private environmentService: EnvironmentService,
    private ref: DynamicDialogRef,
    private authService: AuthService,
    private notificationService: NotificationService,
    private authUtilService: AuthUtilService
  ) {}

  ngOnInit() {
    this.authService.timeoutDialogOpen = true;

    this.form = new FormGroup({
      username: new FormControl(),
      password: new FormControl(),
    });

    this.authService.authenticated$
      .pipe(
        filter((value) => value === false),
        takeUntil(this.destroy$)
      )
      .subscribe(() => this.ref.close(null));

    this.authService.sessionTimeout$
      .pipe(
        filter((value) => value === false),
        takeUntil(this.destroy$)
      )
      .subscribe(() => this.ref.close(null));

    // login failure
    this.authService.loginError$
      .pipe(
        filter((x) => !!x),
        takeUntil(this.destroy$)
      )
      .subscribe((error) => {
        this.form.get('password').setValue(null);
        this.form.get('password').setErrors(null);
        this.form.get('password').markAsUntouched();
        this.form.get('password').markAsPristine();
        this.form.enable();

        const { title, message } = this.authUtilService.getErrorMessage(error);

        this.notificationService.error({
          title,
          message,
        });
      });
  }

  ngOnDestroy() {
    this.authService.timeoutDialogOpen = false;

    this.destroy$.next();
    this.destroy$.unsubscribe();
  }

  login() {
    if (!this.form.get('username').value || !this.form.get('password').value) {
      if (!this.form.get('username').value) {
        this.form.get('username').setErrors({ required: true });
      }

      if (!this.form.get('password').value) {
        this.form.get('password').setErrors({ required: true });
      }

      this.notificationService.error({
        message: this.translate('auth.login.error_messages.fields_cannot_be_blank'),
      });

      return;
    }

    const payload: LoginRequest = {
      username: this.form.get('username').value,
      password: this.form.get('password').value,
    };

    this.store.dispatch(AuthActions.LoginFromTimeout({ ...payload }));
  }

  cancel() {
    this.store.dispatch(AuthActions.Logout());
    this.ref.close(null);
  }

  reset() {
    this.form.setValue({
      username: null,
      password: null,
    });
  }

  automaticLogin = () => {
    if (this.environmentService.environment.production) {
      return;
    }

    this.form.setValue(this.environmentService.credentials);
  };
}
